import React, { useState, useEffect, useContext } from 'react';
import Router from 'next/router';
import styled from 'styled-components';
import nookies from 'nookies';
import { useRouter } from 'next/router';
import Button from '../components/Button';
import FloatingButton from '../components/FloatingButton';
import { PageElements, P, SmallP } from '../components/PageElements';
import {
  setLocale as setCookieLocale,
  getCountryCookie,
  setServerSideCookie,
} from '../utils/cookie';
import { CheckButton } from '../components/CheckButton';
import { countries } from '../components/i18n';
import { isServerRender } from '../utils/url';
import { PeikkoContext } from '../contexts/PeikkoContext';
import Analytics from '../utils/analytics';
import { hardNavigate } from '../utils/url';

const LocaleContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Locale = ({ countryCode }) => {
  const { i18n } = useContext(PeikkoContext);
  const [locale, setLocale] = useState<string>(null);
  const country = countries.find((c) => {
    return countryCode === c.shortname;
  });

  const updateLocale = (newLocale: string): void => {
    setLocale(newLocale);
    i18n.updateLocale(newLocale);
    setCookieLocale(newLocale);
    Analytics.setLocale(newLocale);
  };

  useEffect(() => {
    if (country?.languages?.length > 0 && !locale) {
      const newLocale: string = country.languages[0].locale;
      i18n.updateLocale(newLocale);
      // set locale automatically if there's only 1 language supported for country
      if (country?.languages?.length === 1) {
        setLocale(newLocale);
        setCookieLocale(newLocale);
      }
      Analytics.setLocale(newLocale);
    }
  }, [country, locale, i18n]);

  const router = useRouter();

  useEffect(() => {
    router.prefetch('/onboarding/1');
  }, [router]);

  if (!country) {
    return null;
  }

  return (
    <>
      <PageElements>
        <div className="!mb-28 w-full">
          <P role="heading" aria-level={1}>
            {i18n.t('locale.header')}
          </P>
          <SmallP style={{ marginBottom: '2em' }}>
            {i18n.t('locale.subheader')}
          </SmallP>
          <LocaleContainer>
            {country.languages.map((l) => (
              <CheckButton
                key={l.locale}
                onClick={(): void => updateLocale(l.locale)}
                selected={locale ? locale === l.locale : false}
                buttonText={l.name}
              />
            ))}
          </LocaleContainer>
        </div>
      </PageElements>
      <FloatingButton>
        {/* A full navigation: getRedirect has to see an httpOnly token. */}
        <Button disabled={!locale} onClick={() => hardNavigate('/')}>
          {i18n.t('locale.continue')}
        </Button>
      </FloatingButton>
    </>
  );
};

interface InitialProps {
  countryCode?: string;
}

Locale.getInitialProps = (ctx): InitialProps => {
  const getCountryUsingCookie = (): string => {
    const countryCookie: string = getCountryCookie();
    const countryObj = countries.find((c) => countryCookie === c.shortname);

    return countryObj ? countryObj.shortname : null;
  };

  let country: string | undefined;

  if (isServerRender(ctx)) {
    const queryCountry = ctx.query.country as string | undefined;
    const cookieCountry = nookies.get(ctx).country;

    country = queryCountry || cookieCountry;

    // If country came from query param, set it as a cookie for future requests
    if (queryCountry && queryCountry !== cookieCountry) {
      setServerSideCookie(ctx, 'country', queryCountry, 28);
    }
  } else {
    country = getCountryUsingCookie();
  }

  if (!country) {
    if (isServerRender(ctx)) {
      ctx.res.writeHead(302, { Location: '/country' });
      ctx.res.end();
      return {};
    }
    Router.push('/country');
    return {};
  }

  // Single-language countries (e.g. UK with en-GB only) skip the picker —
  // there's nothing to choose. Set locale cookie and redirect to /.
  const countryObj = countries.find((c) => c.shortname === country);
  if (countryObj && countryObj.languages.length === 1) {
    const onlyLocale = countryObj.languages[0].locale;
    if (isServerRender(ctx)) {
      setServerSideCookie(ctx, 'locale', onlyLocale, 28);
      ctx.res.writeHead(302, { Location: '/' });
      ctx.res.end();
      return {};
    }
    setCookieLocale(onlyLocale);
    Router.replace('/');
    return {};
  }

  return { countryCode: country };
};

export default Locale;
